import { useMemo, useRef, useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import AnalysisDetail from './components/AnalysisDetail';
import AdminSessionPreview from './components/AdminSessionPreview';
import AdminSessionView from './components/AdminSessionView';
import Phase1Input from './components/Phase1Input';
import Phase2PDFUpload from './components/Phase2PDFUpload';
import Phase2FormulaSelection from './components/Phase2FormulaSelection';
import Phase2RealizationSelection from './components/Phase2RealizationSelection';
import Phase3Results from './components/Phase3Results';
import LoadingModal from './components/LoadingModal';
import AlertModal from './components/AlertModal';
import { authAPI, phase3API } from './services/api';
import { AUTH_EVENTS, AUTH_LOCAL_KEYS } from './config/api';
import { LabelsProvider } from './config/labelsContext';
import defaultLabels from './config/labels';
import EditableLabel from './components/EditableLabel';

type WorkflowPhase =
  | 'dashboard'
  | 'detail'
  | 'input'
  | 'pdf_upload'
  | 'formula_selection'
  | 'realization_selection'
  | 'results';

type UiStep = 1 | 2 | 3 | 4;
type ActiveSubStep =
  | 'overview'
  | 'pdf'
  | 'formula'
  | 'collect'
  | 'realization'
  | 'map_generation'
  | 'download'
  | 'completed';

const orderedSubSteps: ActiveSubStep[] = [
  'overview',
  'pdf',
  'formula',
  'collect',
  'realization',
  'map_generation',
  'download',
  'completed',
];

// Active Session Type
interface ActiveSession {
  sessionId: string;
  phase: WorkflowPhase;
  userInput: string;
  updatedAt: Date;
  taskNumber: number;
}

function App() {
  const isAdminPreviewRoute = window.location.pathname === '/admin/session-preview';
  const isAdminSessionViewRoute = window.location.pathname === '/admin/session-view';
  const isAdminRoute = isAdminPreviewRoute || isAdminSessionViewRoute;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<WorkflowPhase>('dashboard');
  const [sessionId, setSessionId] = useState<string>('');
  const [workflowData, setWorkflowData] = useState<Record<string, unknown>>({});
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [user, setUser] = useState({
    username: '',
    email: '',
    role: defaultLabels.app.defaultRole,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(defaultLabels.app.defaultLoadingMessage);
  const [isLoadingModalHidden, setIsLoadingModalHidden] = useState(false);
  const [resultsSubStep, setResultsSubStep] = useState<'download' | 'completed'>('download');
  // Tracks which session "owns" the global loading modal.
  // Prevents showing a different session's loading modal after switching sessions.
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  // Phase1 has no session_id yet; use a draft owner id to scope loading to the current flow.
  const [draftLoadingOwnerId, setDraftLoadingOwnerId] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState<string>(defaultLabels.app.alertDefaultTitle);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const restoredAppStateRef = useRef(false);
  const lastLoadingMessageRef = useRef<string>('');
  const prevIsLoadingRef = useRef(false);
  const loadingOwnerRef = useRef<string | null>(null);
  const loadingGenerationRef = useRef(0);
  const currentPhaseRef = useRef<WorkflowPhase>(currentPhase);

  const makeDraftOwnerId = () =>
    `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  const uiStep = useMemo<UiStep>(() => {
    if (currentPhase === 'input' || currentPhase === 'pdf_upload') return 1;
    if (currentPhase === 'formula_selection') return 2;
    if (currentPhase === 'realization_selection') return 3;
    return 4;
  }, [currentPhase]);

  const stepStatus = (step: UiStep) => {
    if (step === 4 && uiStep === 4 && activeSubStep === 'completed') return 'completed';
    if (uiStep === step) return 'active';
    if (uiStep > step) return 'completed';
    return 'pending';
  };

  const activeSubStep = useMemo<ActiveSubStep>(() => {
    if (currentPhase === 'input') return 'overview';
    if (currentPhase === 'pdf_upload') return 'pdf';
    if (currentPhase === 'formula_selection') {
      return isLoading && /(特許検索|特許を収集|条件に合う特許)/.test(loadingMessage)
        ? 'collect'
        : 'formula';
    }
    if (currentPhase === 'realization_selection') {
      return isLoading &&
        /(特許分析を実行中|構成要素を抽出中|特許品質を評価中|特許マップに載せる技術|特許マップに必要な情報|各技術を構成する要素|重要度を評価)/.test(
          loadingMessage
        )
        ? 'map_generation'
        : 'realization';
    }
    return resultsSubStep;
  }, [currentPhase, isLoading, loadingMessage, resultsSubStep]);

  const subStepStatus = (subStep: ActiveSubStep) => {
    if (uiStep === 4 && activeSubStep === 'download') {
      if (subStep === 'download') return 'completed';
      if (subStep === 'completed') return '';
    }
    if (uiStep === 4 && activeSubStep === 'completed') {
      if (subStep === 'download' || subStep === 'completed') return 'completed';
    }

    const currentIndex = orderedSubSteps.indexOf(activeSubStep);
    const targetIndex = orderedSubSteps.indexOf(subStep);

    if (currentIndex === -1 || targetIndex === -1) return '';
    if (targetIndex < currentIndex) return 'completed';
    if (targetIndex === currentIndex) return 'current';
    return '';
  };

  useEffect(() => {
    if (currentPhase !== 'results' && currentPhase !== 'detail') {
      setResultsSubStep('download');
    }
  }, [currentPhase, sessionId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentPhase, sessionId]);

  useEffect(() => {
    currentPhaseRef.current = currentPhase;
  }, [currentPhase]);

  const activeLoadingOwnerId = useMemo(() => {
    if (currentPhase === 'input') return draftLoadingOwnerId;
    return sessionId || null;
  }, [currentPhase, draftLoadingOwnerId, sessionId]);

  // Keep a ref for sync ownership checks in async callbacks.
  useEffect(() => {
    loadingOwnerRef.current = loadingSessionId;
  }, [loadingSessionId]);

  // Ensure Phase1 always has a draft owner id while mounted.
  useEffect(() => {
    if (currentPhase !== 'input') {
      if (draftLoadingOwnerId) setDraftLoadingOwnerId(null);
      return;
    }
    if (!draftLoadingOwnerId) setDraftLoadingOwnerId(makeDraftOwnerId());
  }, [currentPhase, draftLoadingOwnerId]);

  const beginOwnedLoading = (ownerId: string, message: string) => {
    if (!ownerId) return;
    loadingGenerationRef.current += 1;
    loadingOwnerRef.current = ownerId;
    setLoadingSessionId(ownerId);
    setIsLoading(true);
    setIsLoadingModalHidden(false);
    setLoadingMessage(message);
  };

  const endOwnedLoading = (ownerId: string) => {
    if (!ownerId) return;
    // Prefer the state value for safety; refs can become stale across async flows.
    // Only refuse to end loading if we are sure another owner is active.
    const activeOwner = (loadingSessionId || loadingOwnerRef.current || '').toString();
    if (activeOwner && activeOwner !== ownerId) return;
    setIsLoading(false);
    setLoadingSessionId(null);
    setLoadingMessage('');
  };

  const currentSessionStorageKey = useMemo(() => {
    if (!user.username) return null;
    return `currentSession_${user.username}`;
  }, [user.username]);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchUserInfo();
    }
  }, []);

  // If any API call detects an expired token (401), force logout in the same tab.
  useEffect(() => {
    const onExpired = () => {
      // Reuse the existing logout routine for a full reset.
      handleLogout();
    };
    window.addEventListener(AUTH_EVENTS.expired, onExpired as EventListener);
    return () => {
      window.removeEventListener(AUTH_EVENTS.expired, onExpired as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserInfo = async () => {
    try {
      const userInfo = await authAPI.getCurrentUser();
      setUser(userInfo);
      setIsAuthenticated(true);
    } catch (e) {
      // ユーザー情報の取得に失敗
      localStorage.removeItem(AUTH_LOCAL_KEYS.token);
      localStorage.removeItem(AUTH_LOCAL_KEYS.refreshToken);
      localStorage.removeItem(AUTH_LOCAL_KEYS.username);
      setIsAuthenticated(false);
    }
  };

  const handleLoginSuccess = (userInfo: { username: string; email: string; role: string }) => {
    setUser(userInfo);
    setIsAuthenticated(true);
  };

  // Load active sessions on mount (depend on user.username)
  useEffect(() => {
    if (!user.username || !isAuthenticated) return;

    // 1. Load from localStorage first (for persistence across refresh)
    const savedSessions = localStorage.getItem(`activeSessions_${user.username}`);
    if (savedSessions) {
      try {
        const parsed = JSON.parse(savedSessions) as Array<{
          sessionId: string;
          phase: WorkflowPhase;
          userInput: string;
          updatedAt: string;
          taskNumber: number;
        }>;
        // Date strings back to Date objects
        const hydrated = parsed.map((s): ActiveSession => ({
          ...s,
          updatedAt: new Date(s.updatedAt),
        }));
        setActiveSessions(hydrated);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(defaultLabels.app.sessionLoadError, e);
      }
    }

    // 2. In a real app, fetch from API to sync
    // api.get(`/auth/history/${user.username}`).then(...)
  }, [user.username, isAuthenticated]);

  // Save to localStorage whenever activeSessions changes
  useEffect(() => {
    if (user.username && isAuthenticated) {
      localStorage.setItem(`activeSessions_${user.username}`, JSON.stringify(activeSessions));
    }
  }, [activeSessions, user.username, isAuthenticated]);

  // Allow user to temporarily hide the loading modal overlay.
  // If loading starts (false -> true) or loading ends, we show it again.
  useEffect(() => {
    if (!isLoading) {
      setIsLoadingModalHidden(false);
      lastLoadingMessageRef.current = '';
      prevIsLoadingRef.current = false;
      setLoadingSessionId(null);
      return;
    }
    // Show modal when loading starts (false -> true)
    if (!prevIsLoadingRef.current && isLoading) {
      setIsLoadingModalHidden(false);
    }
    // Do NOT force-show the modal when message changes.
    // Users may choose to hide the overlay (e.g. while polling for Excel download URL).
    // Keep it hidden until the current loading finishes, unless loading restarts.
    lastLoadingMessageRef.current = loadingMessage;
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, loadingMessage]);

  // If we switch sessions/phases, hide another owner's loading only on dashboard/detail.
  // Workflow screens must still be able to show the modal for the session they just resumed.
  useEffect(() => {
    if (!isLoading) return;
    if (!activeLoadingOwnerId) return;
    const isNonWorkflowView = currentPhase === 'dashboard' || currentPhase === 'detail';
    if (isNonWorkflowView && loadingSessionId && loadingSessionId !== activeLoadingOwnerId) {
      setIsLoadingModalHidden(true);
    }
  }, [activeLoadingOwnerId, currentPhase, isLoading, loadingSessionId]);

  // Persist the currently opened session/phase so refresh can restore it.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!currentSessionStorageKey) return;

    // Only persist when we are in the workflow (not dashboard/detail).
    if (!sessionId || !currentPhase || currentPhase === 'dashboard' || currentPhase === 'detail') {
      return;
    }

    const active = activeSessions.find((s) => s.sessionId === sessionId);
    const payload = {
      sessionId,
      phase: currentPhase,
      userInput: (active?.userInput || (workflowData.userInput as string) || '').toString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(currentSessionStorageKey, JSON.stringify(payload));
  }, [
    activeSessions,
    currentPhase,
    currentSessionStorageKey,
    isAuthenticated,
    sessionId,
    workflowData.userInput,
  ]);

  // Rehydrate current session/phase on refresh.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!currentSessionStorageKey) return;
    if (restoredAppStateRef.current) return;

    const raw = localStorage.getItem(currentSessionStorageKey);
    if (!raw) {
      restoredAppStateRef.current = true;
      return;
    }

    try {
      const parsed = JSON.parse(raw) as {
        sessionId?: string;
        phase?: WorkflowPhase;
        userInput?: string;
      };
      const sid = (parsed.sessionId || '').toString();
      const phase = parsed.phase;
      if (!sid || !phase) {
        restoredAppStateRef.current = true;
        return;
      }

      // Don't auto-restore phase on page refresh - always start at dashboard
      // User can click "再開" button to resume a session
      // Only restore sessionId for reference, but stay on dashboard
      restoredAppStateRef.current = true;
      // setSessionId(sid);  // Don't restore - let user choose from dashboard
      // setCurrentPhase(phase);  // Don't restore - always go to dashboard
      if (parsed.userInput) {
        setWorkflowData((prev) => ({ ...prev, userInput: parsed.userInput }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(defaultLabels.app.localStorageRestoreError, e);
    } finally {
      restoredAppStateRef.current = true;
    }
  }, [currentSessionStorageKey, isAuthenticated]);

  const handlePhase1Complete = (sid: string, userInput: string, title?: string) => {
    if (currentPhaseRef.current !== 'input') return;
    setDraftLoadingOwnerId(null);
    setSessionId(sid);
    setWorkflowData({ ...workflowData, userInput, title });
    setCurrentPhase('pdf_upload');

    // Add to active sessions if not exists
    if (!activeSessions.find((s) => s.sessionId === sid)) {
      // Calculate next task number
      const nextTaskNumber = activeSessions.length + 1;
      setActiveSessions((prev) => [
        {
          sessionId: sid,
          phase: 'pdf_upload',
          userInput,
          updatedAt: new Date(),
          taskNumber: nextTaskNumber,
        },
        ...prev,
      ]);
    }
  };

  const updateSessionPhase = (newPhase: WorkflowPhase) => {
    // Bump generation so the outgoing Phase component's scoped setIsLoading(false)
    // cannot kill the incoming Phase component's loading state.
    loadingGenerationRef.current += 1;
    setCurrentPhase(newPhase);
    // Remove from active sessions when the workflow reaches the completed detail view.
    if (newPhase === 'results' || newPhase === 'detail') {
      setActiveSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    } else {
      setActiveSessions((prev) =>
        prev.map((s) =>
          s.sessionId === sessionId ? { ...s, phase: newPhase, updatedAt: new Date() } : s
        )
      );
    }
  };

  const handlePhase2PDFComplete = (data: unknown) => {
    if (currentPhaseRef.current !== 'pdf_upload') return;
    setWorkflowData({ ...workflowData, pdfData: data });
    updateSessionPhase('formula_selection');
  };

  const handleFormulaSelectionComplete = (data: unknown) => {
    if (currentPhaseRef.current !== 'formula_selection') return;
    setWorkflowData({ ...workflowData, formulaData: data });
    updateSessionPhase('realization_selection');
  };

  const handleRealizationSelectionComplete = (data: unknown) => {
    if (currentPhaseRef.current !== 'realization_selection') return;
    setWorkflowData({ ...workflowData, realizationData: data });
    updateSessionPhase('detail');
  };

  const handleStartAnalysis = () => {
    // Starting a new flow: clear any previous global loading state.
    setIsLoading(false);
    setLoadingSessionId(null);
    setLoadingMessage(defaultLabels.app.defaultLoadingMessage);
    setIsLoadingModalHidden(false);
    setDraftLoadingOwnerId(makeDraftOwnerId());
    setCurrentPhase('input');
    setSessionId('');
    setWorkflowData({});
  };

  const handleRestart = () => {
    setDraftLoadingOwnerId(null);
    // Remove completed session from active sessions
    if (sessionId) {
      setActiveSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    }
    if (currentSessionStorageKey) localStorage.removeItem(currentSessionStorageKey);

    setCurrentPhase('dashboard');
    setSessionId('');
    setWorkflowData({});
  };

  const handleViewResult = (id: string) => {
    setDraftLoadingOwnerId(null);
    // View detailed analysis — already completed, so show Step4 as done immediately.
    setResultsSubStep('completed');
    setSessionId(id);
    setCurrentPhase('detail');
  };

  const phaseFromStep = (step: number): WorkflowPhase => {
    // Backend step semantics:
    //   <4   : initial input (great-cause / domain)
    //   4-7  : PDF upload + analysis + keyword extraction (Phase2PDFUpload)
    //   8+   : keyword classification done; ready for / in formula generation
    //          (Phase2FormulaSelection — its resumeOrStartFormulaGeneration
    //          breaks out of its wait loop at step >= 8). The backend
    //          generate_formulas job often runs while step is still 8, so
    //          mapping 8 to 'pdf_upload' caused a brief Phase2PDFUpload
    //          mount that immediately advanced to formula_selection.
    //   15+  : final search done; ready for realization selection
    //   19+  : workflow complete (detail view)
    const s = Number(step || 0);
    if (s >= 19) return 'detail';
    if (s >= 15) return 'realization_selection';
    if (s >= 8) return 'formula_selection';
    if (s >= 4) return 'pdf_upload';
    return 'input';
  };

  const handleOpenSession = async (id: string, _hintStep?: number) => {
    // _hintStep is retained for API compatibility but is no longer used.
    // The previous implementation transitioned the phase *before* getSession
    // completed (optimistic navigation based on the cached step). That caused
    // two problems:
    //   1) When the cached step was stale (e.g. 0), the user briefly saw a
    //      wrong phase like Step1 "分野の指定", and if the subsequent
    //      getSession call failed/aborted the catch branch returned without
    //      correcting the phase, leaving the user stuck on the wrong screen.
    //   2) It opened a race window with handleLoadingModalClose's async
    //      pre-fetch when users clicked "管理画面へ" then "実行画面へ" in
    //      quick succession.
    // Always wait for the backend response and navigate based on the real
    // current_step. The LoadingModal stays visible during the fetch so the
    // click feels responsive even though navigation is delayed by one
    // network round-trip.
    void _hintStep;

    setDraftLoadingOwnerId(null);
    // Set sessionId synchronously *before* the fetch so the LoadingModal's
    // owner-tracking effect (lines 302-308) sees a matching activeLoadingOwnerId
    // and doesn't hide the modal during the await. Without this, the dashboard's
    // `currentPhase === 'dashboard'` view considered our fresh loadingSessionId
    // a "different owner" and hid the modal — so the user only saw the spinner
    // button until the fetch resolved.
    setSessionId(id);
    // Show the loading modal so the user has visual feedback during the fetch.
    beginOwnedLoading(id, defaultLabels.app.sessionLoadingMessage);

    try {
      const session = await phase3API.getSession(id);
      const nextPhase = phaseFromStep(session.current_step);

      setWorkflowData((prev) => ({
        ...prev,
        title: session.title || '',
        userInput: prev.userInput || session.user_input || '',
      }));

      // For completed sessions (detail phase), show Step4 as completed immediately.
      if (nextPhase === 'detail') {
        setResultsSubStep('completed');
      }

      setCurrentPhase(nextPhase);

      // formula_selection and realization_selection manage their own loading
      // modal via scoped setIsLoading — keep the modal alive for them so
      // their internal fetch can take over without a flicker. All other
      // phases need the loading modal dismissed here.
      if (nextPhase !== 'formula_selection' && nextPhase !== 'realization_selection') {
        endOwnedLoading(id);
      }
    } catch (e) {
      setAlertTitle(defaultLabels.app.errorTitle);
      setAlertMessage(
        e instanceof Error && e.message ? e.message : defaultLabels.app.sessionReadError
      );
      setAlertOpen(true);
      setSessionId('');
      setCurrentPhase('dashboard');
      endOwnedLoading(id);
    }
  };

  const handleDeleteSession = async (id: string) => {
    try {
      await phase3API.deleteSession(id);
      // If we deleted the currently open session, return to dashboard.
      if (sessionId === id) {
        setSessionId('');
        setWorkflowData({});
        setCurrentPhase('dashboard');
      }
    } catch (e) {
      setAlertTitle(defaultLabels.app.errorTitle);
      setAlertMessage(defaultLabels.app.sessionDeleteError);
      setAlertOpen(true);
    }
  };

  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  const releaseDashboardViewOwner = (ownerId: string | null) => {
    const owner = (ownerId || '').toString();
    setDraftLoadingOwnerId(null);
    if (owner) {
      endOwnedLoading(owner);
    }
    setDashboardRefreshKey((k) => k + 1);
    setCurrentPhase('dashboard');
  };

  const handleBackToDashboard = () => {
    releaseDashboardViewOwner(sessionId || null);
  };

  const handleNavigate = (page: 'dashboard' | 'analysis') => {
    if (page === 'dashboard') {
      releaseDashboardViewOwner(sessionId || null);
    } else {
      handleStartAnalysis();
    }
  };

  const getCurrentPage = (): 'dashboard' | 'analysis' => {
    return currentPhase === 'dashboard' || currentPhase === 'detail' ? 'dashboard' : 'analysis';
  };

  const handleLogout = () => {
    // Clear auth token
    localStorage.removeItem(AUTH_LOCAL_KEYS.token);
    localStorage.removeItem(AUTH_LOCAL_KEYS.refreshToken);
    localStorage.removeItem(AUTH_LOCAL_KEYS.username);

    // Clear session data
    setSessionId('');
    setWorkflowData({});
    setActiveSessions([]);
    setCurrentPhase('dashboard');
    setUser({ username: '', email: '', role: defaultLabels.app.defaultRole });
    setIsLoading(false);
    setLoadingMessage(defaultLabels.app.defaultLoadingMessage);
    setIsLoadingModalHidden(false);
    setLoadingSessionId(null);
    setDraftLoadingOwnerId(null);
    setIsAuthenticated(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const showAlert = (message: string, title: string = '') => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const scopedSetIsLoading = (ownerSessionId: string | null) => {
    const generation = loadingGenerationRef.current;
    return (loading: boolean) => {
      const owner = (ownerSessionId || '').toString();
      if (!owner) return;
      if (loading) {
        loadingOwnerRef.current = owner;
        setIsLoading(true);
        setLoadingSessionId(owner);
        return;
      }
      // Stale cleanup guard: if a newer beginOwnedLoading has been called since this
      // scoped setter was created, skip the cleanup to avoid killing the new loading state.
      if (loadingGenerationRef.current !== generation) return;
      endOwnedLoading(owner);
    };
  };

  const scopedSetLoadingMessage = (ownerSessionId: string | null) => {
    const generation = loadingGenerationRef.current;
    return (message: string) => {
      const owner = (ownerSessionId || '').toString();
      if (!owner) return;
      if (loadingGenerationRef.current !== generation) return;
      if (loadingOwnerRef.current !== owner) return;
      setLoadingMessage(message);
    };
  };

  // Show the loading modal whenever a phase reports "busy".
  // We still avoid cross-session leaks by force-clearing `isLoading` when ownership changes.
  // (see the effect: "If we switch sessions/phases...")
  //
  // The dashboard itself never sets isLoading=true on its own; it only becomes
  // true while the user is navigating *out* of the dashboard (handleOpenSession's
  // beginOwnedLoading). Showing the modal there is desirable so the click feels
  // responsive — without this the user sees only the spinner button and the
  // session-load fetch happens silently in the background.
  // The 'detail' view continues to suppress the modal (it manages its own UI).
  const shouldShowLoadingModal = isLoading && !isLoadingModalHidden && currentPhase !== 'detail';

  // UX: Always guide users back to the dashboard from any loading step.
  const loadingCloseLabel = defaultLabels.app.loadingCloseLabel;
  const handleLoadingModalClose = async () => {
    setDraftLoadingOwnerId(null);
    setDashboardRefreshKey((k) => k + 1);
    setCurrentPhase('dashboard');
    setIsLoadingModalHidden(true);
  };

  return (
    <LabelsProvider isAuthenticated={isAuthenticated}>
      <div className="App">
        {shouldShowLoadingModal && (
          <LoadingModal
            message={loadingMessage}
            onClose={
              loadingMessage === defaultLabels.app.sessionLoadingMessage
                ? undefined
                : handleLoadingModalClose
            }
            closeLabel={loadingCloseLabel}
          />
        )}
        <AlertModal
          open={alertOpen}
          title={alertTitle}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />

        <Header
          currentPage={getCurrentPage()}
          onNavigate={handleNavigate}
          user={user}
          onLogout={handleLogout}
          isLoading={isLoading}
        />

        {isAdminPreviewRoute ? (
          <AdminSessionPreview />
        ) : isAdminSessionViewRoute ? (
          <AdminSessionView />
        ) : currentPhase === 'dashboard' ? (
          <Dashboard
            onStartAnalysis={handleStartAnalysis}
            onViewResult={handleViewResult}
            onOpenSession={handleOpenSession}
            onDeleteSession={handleDeleteSession}
            showAlert={showAlert}
            userId={user.username}
            refreshKey={dashboardRefreshKey}
          />
        ) : null}

        {!isAdminRoute && currentPhase !== 'dashboard' && (
          <div className="container">
            <div
              className={`workflow-header${currentPhase === 'detail' ? ' workflow-header-detail' : ''}`}
            >
              <h1>
                <EditableLabel
                  labelKey="app.workflowTitle"
                  defaultValue="特許マップ作成 進行状況"
                />
              </h1>
              <div className="phase-indicator">
                <div className={`phase-step ${stepStatus(1)}`}>
                  <div className="phase-step-top">
                    <span className="phase-step-num">
                      <EditableLabel labelKey="app.step1Num" defaultValue="Step 1" />
                    </span>
                    <span className="phase-step-title">
                      <EditableLabel labelKey="app.step1Title" defaultValue="分野の指定" />
                    </span>
                  </div>
                  <ul className="phase-step-list">
                    <li className={subStepStatus('overview')}>
                      <EditableLabel
                        labelKey="app.step1SubOverview"
                        defaultValue="概要を入力"
                        as="span"
                      />
                    </li>
                    <li className={subStepStatus('pdf')}>
                      <EditableLabel
                        labelKey="app.step1SubPdf"
                        defaultValue="特許例を入力"
                        as="span"
                      />
                    </li>
                  </ul>
                </div>
                <div className={`phase-step ${stepStatus(2)}`}>
                  <div className="phase-step-top">
                    <span className="phase-step-num">
                      <EditableLabel labelKey="app.step2Num" defaultValue="Step 2" />
                    </span>
                    <span className="phase-step-title">
                      <EditableLabel labelKey="app.step2Title" defaultValue="特許情報の収集" />
                    </span>
                  </div>
                  <ul className="phase-step-list">
                    <li className={subStepStatus('formula')}>
                      <EditableLabel
                        labelKey="app.step2SubFormula"
                        defaultValue="特許検索式を選択"
                        as="span"
                      />
                    </li>
                    <li className={subStepStatus('collect')}>
                      <EditableLabel
                        labelKey="app.step2SubCollect"
                        defaultValue="特許を自動収集。技術項目を抽出"
                        as="span"
                      />
                    </li>
                  </ul>
                </div>
                <div className={`phase-step ${stepStatus(3)}`}>
                  <div className="phase-step-top">
                    <span className="phase-step-num">
                      <EditableLabel labelKey="app.step3Num" defaultValue="Step 3" />
                    </span>
                    <span className="phase-step-title">
                      <EditableLabel labelKey="app.step3Title" defaultValue="特許マップ作成" />
                    </span>
                  </div>
                  <ul className="phase-step-list">
                    <li className={subStepStatus('realization')}>
                      <EditableLabel
                        labelKey="app.step3SubRealization"
                        defaultValue="特許マップに掲載する対象技術を選択"
                        as="span"
                      />
                    </li>
                    <li className={subStepStatus('map_generation')}>
                      <EditableLabel
                        labelKey="app.step3SubMapGeneration"
                        defaultValue="特許マップ自動生成"
                        as="span"
                      />
                    </li>
                  </ul>
                </div>
                <div className={`phase-step ${stepStatus(4)}`}>
                  <div className="phase-step-top">
                    <span className="phase-step-num">
                      <EditableLabel labelKey="app.step4Num" defaultValue="Step 4" />
                    </span>
                    <span className="phase-step-title">
                      <EditableLabel
                        labelKey="app.step4Title"
                        defaultValue="特許マップ ダウンロード"
                      />
                    </span>
                  </div>
                  <ul className="phase-step-list">
                    <li className={subStepStatus('download')}>
                      <EditableLabel
                        labelKey="app.step4SubDownload"
                        defaultValue="ダウンロード"
                        as="span"
                      />
                    </li>
                    <li className={subStepStatus('completed')}>
                      <EditableLabel
                        labelKey="app.step4SubCompleted"
                        defaultValue="完了"
                        as="span"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {currentPhase === 'input' && (
              <Phase1Input
                onComplete={handlePhase1Complete}
                username={user.username}
                showAlert={showAlert}
              />
            )}

            {currentPhase === 'pdf_upload' && (
              <Phase2PDFUpload
                sessionId={sessionId}
                onComplete={handlePhase2PDFComplete}
                setIsLoading={scopedSetIsLoading(sessionId || null)}
                setLoadingMessage={scopedSetLoadingMessage(sessionId || null)}
                showAlert={showAlert}
              />
            )}

            {currentPhase === 'formula_selection' && (
              <Phase2FormulaSelection
                sessionId={sessionId}
                onComplete={handleFormulaSelectionComplete}
                setIsLoading={scopedSetIsLoading(sessionId || null)}
                setLoadingMessage={scopedSetLoadingMessage(sessionId || null)}
                showAlert={showAlert}
              />
            )}

            {currentPhase === 'realization_selection' && (
              <Phase2RealizationSelection
                sessionId={sessionId}
                onComplete={handleRealizationSelectionComplete}
                setIsLoading={scopedSetIsLoading(sessionId || null)}
                setLoadingMessage={scopedSetLoadingMessage(sessionId || null)}
                showAlert={showAlert}
              />
            )}

            {currentPhase === 'results' && (
              <Phase3Results
                sessionId={sessionId}
                workflowData={workflowData}
                onRestart={handleRestart}
                onStatusChange={setResultsSubStep}
                setIsLoading={scopedSetIsLoading(sessionId || null)}
                setLoadingMessage={scopedSetLoadingMessage(sessionId || null)}
                showAlert={showAlert}
              />
            )}
          </div>
        )}

        {!isAdminRoute && currentPhase === 'detail' && (
          <AnalysisDetail
            sessionId={sessionId}
            onBack={handleBackToDashboard}
            title={(workflowData.title as string | undefined) || ''}
            onStatusChange={setResultsSubStep}
          />
        )}
      </div>
    </LabelsProvider>
  );
}

export default App;
